import { FC, useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { useParams } from "react-router-dom";
import { Formik, useFormik } from "formik";
import { DateTime } from "luxon";
import { t } from "i18next";

import { InputFile } from "@/shared/components/atoms/InputFile";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import PaginationUI from "@/shared/components/atoms/PaginationUI";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import TableListExercises from "@/shared/components/organisms/TableListExercises";

import { useExercisesQuery } from "@/app/exercises/exercises.api";
import {
  useCreatePersonalizedExerciseMutation,
  useDeletePersonalizedExerciseMutation,
} from "@/app/exercises/personalized-exercise.api";
import { PersonalizedExerciseValues } from "@/app/exercises/exercises";
import { toasts } from "@/shared/utils/toasts";
import { useDebounce } from "@/shared/hooks/debounce";
import { Page } from "@/shared/components/atoms/Page";

export const Exercises: FC<any> = () => {
  const { id } = useParams();

  const [currentExercise, setCurrentExercise] = useState(null);
  const [createDialogVisible, setCreateDialogVisible] = useState(false);

  const [createPersonalizedExercise, createPersonalizedExerciseResult] =
    useCreatePersonalizedExerciseMutation();
  const [deletePersonalizedExercise, deletePersonalizedExerciseResult] =
    useDeletePersonalizedExerciseMutation();

  const searchFormik = useFormik({
    initialValues: {
      search: "",
      muscle: undefined,
      type: undefined,
      equipment: undefined,
      page: 1,
      branchId: id,
    },
    onSubmit: () => {},
  });

  const debouncedSearch = useDebounce(searchFormik.values, 300);

  const exercisesQuery = useExercisesQuery({
    search: debouncedSearch.search,
    muscle: debouncedSearch.muscle,
    type: debouncedSearch.type,
    branchId: debouncedSearch.branchId,
    page: debouncedSearch.search ? 1 : searchFormik.values.page,
    equipment: debouncedSearch.equipment,
  });

  const personalizedExerciseFormik = useFormik({
    initialValues: {
      image: undefined,
      videoUrl: "",
      video: "",
      exerciseId: undefined,
    },
    onSubmit: (values: PersonalizedExerciseValues) => {
      createPersonalizedExercise({
        ...values,
        branchId: id,
      });
    },
  });

  const handleCreate = () => {
    setCreateDialogVisible(!createDialogVisible);
  };

  const handleEdit = (exercise) => {
    setCurrentExercise(exercise);
    setCreateDialogVisible(true);
  };

  const handleDelete = useCallback(
    (exercise) => {
      deletePersonalizedExercise({
        branchId: id,
        exerciseId: exercise.id,
      });
    },
    [id]
  );

  useEffect(() => {
    if (createPersonalizedExerciseResult.isSuccess) {
      toasts.success(
        t(
          "contexts.personalized_exercises.update_personalized_exercise_success"
        ),
        t(
          "contexts.personalized_exercises.update_personalized_exercise_success_description"
        )
      );
      setCreateDialogVisible(false);
      setCurrentExercise(null);

      exercisesQuery.refetch();
      createPersonalizedExerciseResult.reset();
    }

    if (createPersonalizedExerciseResult.isError) {
      toasts.danger(
        t(
          "contexts.personalized_exercises.update_personalized_exercise_failure"
        ),
        t(
          "contexts.personalized_exercises.update_personalized_exercise_failure_description"
        )
      );

      createPersonalizedExerciseResult.reset();
    }
  }, [createPersonalizedExerciseResult]);

  useEffect(() => {
    if (deletePersonalizedExerciseResult.isSuccess) {
      toasts.success(
        t(
          "contexts.personalized_exercises.delete_personalized_exercise_success"
        ),
        t(
          "contexts.personalized_exercises.delete_personalized_exercise_success_description"
        )
      );
      deletePersonalizedExerciseResult.reset();
    }

    if (deletePersonalizedExerciseResult.isError) {
      toasts.danger(
        t(
          "contexts.personalized_exercises.delete_personalized_exercise_failure"
        ),
        t(
          "contexts.personalized_exercises.delete_personalized_exercise_failure_description"
        )
      );
      deletePersonalizedExerciseResult.reset();
    }
  }, [deletePersonalizedExerciseResult]);

  useEffect(() => {
    if (currentExercise?.id) {
      personalizedExerciseFormik.setFieldValue(
        "exerciseId",
        currentExercise.id
      );
    }

    if (currentExercise?.personalizedVideo) {
      const match = currentExercise?.personalizedVideo?.match(
        /youtube\.com\/embed\/([^/?]+)/
      );

      const videoKey = match ? match[1] : null;

      if (videoKey) {
        personalizedExerciseFormik.setFieldValue(
          "videoUrl",
          `https://www.youtube.com/watch?v=${videoKey}`
        );
      }
    }
  }, [currentExercise]);

  const videoKey = useMemo(() => {
    try {
      const url = new URL(personalizedExerciseFormik.values.videoUrl);
      return url.searchParams.get("v");
    } catch (error) {
      return "";
    }
  }, [personalizedExerciseFormik.values.videoUrl]);

  useEffect(() => {
    if (videoKey) {
      personalizedExerciseFormik.setFieldValue(
        "video",
        `https://www.youtube.com/embed/${videoKey}`
      );
    }
  }, [videoKey]);

  return (
    <Fragment>
      <Page
        title={t("contexts.personalized_exercises.title")}
        description={t("contexts.personalized_exercises.subtitle")}
      >
        <TableListExercises
          exercises={exercisesQuery.data?.data || []}
          onChange={searchFormik.handleChange}
          search={searchFormik.values.search}
          muscle={searchFormik.values.muscle}
          equipment={searchFormik.values.equipment}
          type={searchFormik.values.type}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isLoading={exercisesQuery.isLoading || exercisesQuery.isFetching}
        />
        <PaginationUI
          onPageChange={(page) => searchFormik.setFieldValue("page", page)}
          currentPage={debouncedSearch.search ? 1 : searchFormik.values.page}
          pageSize={exercisesQuery.data?.perPage}
          totalResults={exercisesQuery.data?.total}
          totalPages={exercisesQuery.data?.lastPage}
        />
      </Page>
      <DialogCreate
        open={createDialogVisible}
        setOpen={handleCreate}
        tittle={"Nombre de ejercicio"}
        OnclickCreate={personalizedExerciseFormik.handleSubmit}
        OnclickCancel={handleCreate}
        tittleButtonCreate={t("general.edit")}
        tittleButtonCancel={t("general.cancel")}
        isLoading={createPersonalizedExerciseResult.isLoading}
      >
        <form onSubmit={personalizedExerciseFormik.handleSubmit}>
          <div className="mt-4 p-4">
            <div className="space-y-12">
              <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                <div className="flex items-center justify-center h-full text-center">
                  <div>
                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                      {t("user.views.caracteristics_exercise")}
                    </h2>
                  </div>
                </div>
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                  <div className="col-span-full">
                    <InputFile
                      name="image"
                      id="image"
                      label=""
                      span=""
                      spanButton={t("branch.views.upload_image")}
                      description={t("branch.views.description_upload_image")}
                      selectedImage={currentExercise?.imageUrl}
                      error={false}
                      onFileChange={(file) =>
                        personalizedExerciseFormik.setFieldValue("image", file)
                      }
                      onRemoveImage={() =>
                        personalizedExerciseFormik.setFieldValue(
                          "image",
                          undefined
                        )
                      }
                      tittleDelete={""}
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      type="url"
                      label={t("additional-videos.views.url")}
                      placeholder={t("additional-videos.views.url")}
                      name="videoUrl"
                      onChange={personalizedExerciseFormik.handleChange}
                      onBlur={personalizedExerciseFormik.handleBlur}
                      value={personalizedExerciseFormik.values.videoUrl}
                      error={!!personalizedExerciseFormik.errors.videoUrl}
                      textError={personalizedExerciseFormik.errors.videoUrl}
                    />
                  </div>
                  <div className="col-span-full flex items-center justify-center">
                    <div className="aspect-video">
                      {videoKey ? (
                        <div className="bg-gray-300">
                          <iframe
                            width="100%"
                            src={
                              videoKey
                                ? `https://www.youtube.com/embed/${videoKey}`
                                : ""
                            }
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                          ></iframe>
                        </div>
                      ) : (
                        <video
                          className="w-full h-full object-cover"
                          controls
                          autoPlay
                        >
                          <source
                            src={currentExercise?.videoUrl}
                            type="video/mp4"
                          />
                        </video>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </DialogCreate>
    </Fragment>
  );
};
